import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { services } from '../../services/api';
import { toUnixSec } from '../../utils/tradeMarkerBuilder';
import { liveNodeChartValue } from '../../pages/Strategies/Modern/StrategyBuilder/config/nodeVisualConfig';

/**
 * useIndicators Hook
 * Manages technical indicators: state, configuration, and data calculation.
 * 
 * NOTE: For performance, this hook stores raw Map-based data (indicatorMaps).
 * Aligned 1:1 timeline data is generated on-demand via calculateIndicators()
 * to avoid main-thread blockage (170ms+ React updates) during large paginations.
 */
export function useIndicators(
    priceData,
    externalIndicators = [],
    instrument = null,
    timeframe = '5m',
    nodes = [],
    edges = [],
    onError = null,
    isLoading = false,
    context = {}
) {
    const replayMode = context?.mode || null;
    const replayRunId = context?.runId || null;
    const rolloverDays = context?.rolloverDays;
    const instruments = context?.instruments;
    const general = context?.general;
    const expectedStartDate = context?.expectedStartDate;
    const expectedEndDate = context?.expectedEndDate;
    const onBacktestRequired = context?.onBacktestRequired;
    const liveNodeResults = context?.liveNodeResults || null;
    const liveEvalTimeSec = context?.liveEvalTimeSec || null; //--> seconds epoch; null ⇒ no-op
    //--> WS message's primary TF (= deployment timeframe). When it differs from
    //*  the chart's `timeframe`, the live-merge below skips the override — the
    //*  WS value was computed on a different OHLC granularity than the cached
    //*  series and would otherwise create V-shapes at every deployment-TF
    //*  boundary.
    const liveBarTimeframe = context?.liveBarTimeframe || null;
    //! chartMode carries the user-facing mode (live/paper/backtest/rd). Combined
    //! with `isLoading` and `isLoadingPage` below, governs the mount-window gate:
    //! REST fires on mount-stable + left-pagination + strategy edit; right-extension
    //! (forward bar close) is handled by the liveNodeResults WS merge below.
    const chartMode = context?.chartMode || null;
    //! isLoadingPage = useChartData.paginationState.isLoadingPage. Toggles true when
    //! either the auto-edge-trigger (mount: extends initial page → multi-day window)
    //! OR a user pan-left merges a new page. Gate this only during the MOUNT WINDOW
    //! (no prior success); once a calculate has succeeded, let pan-left flow through
    //! to delta-left fetch.
    const isLoadingPage = context?.isLoadingPage || false;

    const [localIndicators, setLocalIndicators] = useState([]);
    // RAW state: timestamp -> value for O(1) lookups
    const [indicatorMaps, setIndicatorMaps] = useState({});
    //--> Exposed loading state so parent can show feedback during indicator recalculation
    const [isCalculating, setIsCalculating] = useState(false);
    const [calculateError, setCalculateError] = useState(null);

    const activeIndicators = useMemo(() => {
        const externals = externalIndicators.map(ind => ({
            ...ind,
            isExternal: true,
            id: ind.id || `ext_${ind.type}_${Date.now()}`
        }));
        return [...externals, ...localIndicators];
    }, [externalIndicators, localIndicators]);

    const prevFunctionalRef = useRef('');
    //--> Track previous node fingerprints and edge signature for incremental change detection
    const prevNodeFingerprintsRef = useRef({});
    const prevEdgeSignatureRef = useRef('');
    const prevActiveIndicatorIdsRef = useRef(new Set());
    const prevStructuralRef = useRef({ instrumentKey: null, timeframe: null, discoveryNodes: '', rolloverDays: null });
    const prevGraphFunctionalKeyRef = useRef('');
    const abortControllerRef = useRef(null);
    const currentDataRangeRef = useRef('');
    const staleWarnedKeyRef = useRef('');
    const activeRequestIdRef = useRef('');
    const lastSuccessfulFunctionalKeyRef = useRef('');
    //--> Track the time range already covered by indicator maps for delta-only pagination requests
    const lastCalcRangeRef = useRef({ startSec: 0, endSec: 0 });

    // helper to extract time in integer seconds
    //--> Uses canonical tradeMarkerBuilder.toUnixSec; preserves Date.now() fallback
    //*   at the boundary for callers that previously relied on it. Math.round →
    //*   Math.floor drift is ≤1s and cannot shift minute-aligned (or coarser) keys.
    const getTimeSeconds = useCallback((d) => {
        if (!d) return Math.floor(Date.now() / 1000);
        const t = toUnixSec(d.time || d.timestamp);
        return t != null ? t : Math.floor(Date.now() / 1000);
    }, []);

    // 1. STRATEGY SIGNATURE (Graph-only)
    const graphFunctionalKey = useMemo(() => {
        const instrumentKey = instrument
            ? (instrument.instrument_id || `${instrument.exchange}:${instrument.symbol}:${instrument.instrument_type || ''}`)
            : null;

        return JSON.stringify({
            indicators: [...activeIndicators]
                .sort((a, b) => String(a.id).localeCompare(String(b.id)))
                .map(ind => ({ id: ind.id, type: ind.type, params: ind.params, nodeType: ind.nodeType })),
            requestedTimeframe: timeframe,
            instrumentKey,
            instruments,
            general,
            // Isolation: Ensure different runs/modes get unique maps to prevent merging corruption
            context: { replayMode, replayRunId, rolloverDays }, 
            functionalNodes: (nodes || [])
                .filter(n => ['math', 'logic', 'logic_gate', 'comparator', 'indicator', 'signal', 'position_trigger', 'constant', 'time_window'].includes(n.type))
                .sort((a, b) => String(a.id).localeCompare(String(b.id)))
                //! Include the full `params` object — the recalc gate must invalidate on ANY nested
                //!  param change (e.g. dee_trend_slope.slope_mode, or any indicator's period/threshold
                //!  that lives under data.params). Omitting it silently suppressed recompute on those.
                .map(n => {
                    const { styleOptions, visual_settings, color, label, ...functionalData } = (n.data || {});
                    return { id: n.id, type: n.type, data: functionalData };
                }),
            discoveryNodes: (nodes || [])
                .filter(n => n.type === 'data_source')
                .sort((a, b) => String(a.id).localeCompare(String(b.id)))
                .map(n => ({ id: n.id, timeframe: n.data?.timeframe })),
            connections: (edges || [])
                .sort((a, b) => String(a.id).localeCompare(String(b.id)))
                .map(e => ({ source: e.source, target: e.target, sH: e.sourceHandle, tH: e.targetHandle }))
        });
    }, [activeIndicators, timeframe, instrument, nodes, edges, replayMode, replayRunId, rolloverDays, instruments, general]);

    // 1b. INCREMENTAL CHANGE DETECTION
    //--> Detect which nodes are new or modified vs the last successful request.
    //--> This enables sending `changed_nodes` to the backend for fast incremental computation.
    const incrementalInfo = useMemo(() => {
        //--> Build current node fingerprints: node_id -> hash of its functional data
        const currentFingerprints = {};
        for (const n of (nodes || [])) {
            const { styleOptions, visual_settings, color, ...funcData } = (n.data || {});
            currentFingerprints[n.id] = JSON.stringify({ type: n.type, data: funcData });
        }
        //--> Include indicator nodes (external indicators mapped as nodes)
        for (const ind of activeIndicators) {
            if (!currentFingerprints[ind.id]) {
                currentFingerprints[ind.id] = JSON.stringify({ type: ind.type, params: ind.params });
            }
        }

        const currentEdgeStrings = (edges || [])
            .sort((a, b) => String(a.id).localeCompare(String(b.id)))
            .map(e => `${e.source}|${e.target}|${e.sourceHandle}|${e.targetHandle}`);
            
        const currentEdgeSig = JSON.stringify(currentEdgeStrings);

        const prevFingerprints = prevNodeFingerprintsRef.current;
        const prevEdgeSig = prevEdgeSignatureRef.current;
        const prevIndicatorIds = prevActiveIndicatorIdsRef.current;

        //--> Determine changed node IDs
        const changedNodeIds = [];
        const currentNodeIds = new Set(Object.keys(currentFingerprints));
        const prevNodeIds = new Set(Object.keys(prevFingerprints));

        for (const id of currentNodeIds) {
            if (!prevFingerprints[id] || prevFingerprints[id] !== currentFingerprints[id]) {
                changedNodeIds.push(id);
            }
        }

        //--> Detect new indicator IDs (not in previous set)
        const currentIndicatorIds = new Set(activeIndicators.map(i => i.id));
        for (const id of currentIndicatorIds) {
            if (!prevIndicatorIds.has(id) && !changedNodeIds.includes(id)) {
                changedNodeIds.push(id);
            }
        }

        //--> Edges changed: identify TARGET nodes of any NEW edges and mark them changed
        const edgesChanged = prevEdgeSig !== '' && prevEdgeSig !== currentEdgeSig;
        if (edgesChanged) {
            try {
                const prevEdgeStrings = JSON.parse(prevEdgeSig);
                const prevEdgeSet = new Set(prevEdgeStrings);
                // Find new edges
                for (const edgeStr of currentEdgeStrings) {
                    if (!prevEdgeSet.has(edgeStr)) {
                        const parts = edgeStr.split('|');
                        if (parts.length >= 2) {
                            const targetId = parts[1];
                            if (!changedNodeIds.includes(targetId)) {
                                changedNodeIds.push(targetId);
                            }
                        }
                    }
                }
            } catch (e) {
                console.error("[useIndicators] Failed to parse edge sig:", e);
            }
        }

        //--> Nodes removed = structural change, not eligible for incremental
        const nodesRemoved = [...prevNodeIds].some(id => !currentNodeIds.has(id));

        //--> Only allow incremental if: prior state exists, no nodes removed
        const canIncremental = prevEdgeSig !== '' && !nodesRemoved && changedNodeIds.length > 0;

        return { currentFingerprints, currentEdgeSig, currentIndicatorIds, changedNodeIds, canIncremental };
    }, [nodes, edges, activeIndicators]);

    // -- Actions --

    const removeIndicator = useCallback((id) => {
        setLocalIndicators(prev => prev.filter(ind => ind.id !== id));
        setIndicatorMaps(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    }, []);

    const updateIndicatorSettings = useCallback((id, newSettings) => {
        setLocalIndicators(prev => prev.map(ind => 
            ind.id === id ? { ...ind, ...newSettings } : ind
        ));
    }, []);


    // 3. REMOTE CALCULATION
    useEffect(() => {
        //! Mount-burst gate. While useChartData is mid-fetch, priceData ref churns
        //! through transient states (1-bar WS seed → first historic page → merged
        //! pages) and parent-render activeIndicators/incrementalInfo refs flip
        //! independently. Without this gate, each transient fires a full-range REST
        //! that the backend processes to completion (TCP abort cannot interrupt
        //! the Python bar-loop), wasting pg_pool conns and saturating Postgres.
        //!
        //! `isLoadingPage` covers the auto-edge-trigger merge that fires AFTER
        //! initial OHLC settles for graphs needing multi-day context. Narrowed to
        //! the MOUNT WINDOW (`!hasPriorSuccess`) so user pan-left after first
        //! success still triggers the delta-left REST fetch.
        const hasPriorSuccess = lastSuccessfulFunctionalKeyRef.current !== '';
        const blockForMountPage = isLoadingPage && !hasPriorSuccess;
        if (!priceData || priceData.length === 0 || activeIndicators.length === 0 || !instrument || isLoading || blockForMountPage) {
            return;
        }

        const isReplayContext = Boolean(replayMode && replayRunId);
        //--\> Include actual priceData extent in the key so pagination triggers recalculation.
        //    The isPagination merge logic (below) correctly accumulates indicator data
        //    without replacing existing values, so repeated fetches are safe.
        const actualStart = getTimeSeconds(priceData[0]);
        const actualEnd = getTimeSeconds(priceData[priceData.length - 1]);
        const dataRange = priceData.length > 0
            ? (isReplayContext
                ? `replay_${expectedStartDate || priceData[0].time}_${expectedEndDate || ''}_${replayRunId || ''}_${actualStart}`
                : (expectedStartDate && expectedEndDate)
                    ? `backtest_${expectedStartDate}_${expectedEndDate}_${actualStart}_${actualEnd}`
                    : `live_${priceData[0].time}_${priceData[priceData.length - 1].time}`)
            : '';
        currentDataRangeRef.current = dataRange;

        const functionalKey = JSON.stringify({ graph: graphFunctionalKey, dataRange });

        if (prevFunctionalRef.current === functionalKey) {
            return;
        }

        const fetchIndicatorData = async () => {
            if (activeRequestIdRef.current === functionalKey) {
                return;
            }
            activeRequestIdRef.current = functionalKey;
            setCalculateError(null);  //--> a new key starts clean; a stale banner never outlives its request

            if (abortControllerRef.current) abortControllerRef.current.abort();
            const controller = new AbortController();
            abortControllerRef.current = controller;
            setIsCalculating(true);

            try {
                let startTimeSec = getTimeSeconds(priceData[0]);
                let endTimeSec = getTimeSeconds(priceData[priceData.length - 1]);

                // Warmup buffer: Ensure 500 bars of history before visible start for indicators
                const tfMatch = (timeframe || '5m').match(/(\d+)([mhd])/);
                let tfSec = 300;
                if (tfMatch) {
                    const val = parseInt(tfMatch[1]);
                    const unit = tfMatch[2];
                    if (unit === 'm') tfSec = val * 60;
                    else if (unit === 'h') tfSec = val * 3600;
                    else if (unit === 'd') tfSec = val * 86400;
                }

                //--> PAGINATION OPTIMIZATION: If this is a pagination extension (strategy unchanged,
                //    data range extended), only request the DELTA range instead of the full 61K+ bars.
                //    This reduces backend OHLC fetch + bar-by-bar evaluation from ~84s to ~10-15s.
                const lastSuccessKey = lastSuccessfulFunctionalKeyRef.current;
                const isStrategyUnchanged = lastSuccessKey && JSON.parse(lastSuccessKey).graph === graphFunctionalKey;
                const lastRange = lastCalcRangeRef.current;
                const hasPriorRange = lastRange.startSec > 0 && lastRange.endSec > 0;

                const isLiveMode = chartMode === 'live' || chartMode === 'paper';

                let isDeltaRequest = false;
                if (isStrategyUnchanged && hasPriorRange) {
                    //--> Delta: only request the range NOT already covered by existing indicator maps
                    //    Add 500-bar warmup for indicator convergence at the boundary
                    const warmupSec = tfSec * 500;
                    if (startTimeSec < lastRange.startSec) {
                        // Pagination extended LEFT: request from new start to old start + overlap
                        endTimeSec = lastRange.startSec + warmupSec;
                        startTimeSec -= warmupSec;
                        isDeltaRequest = true;
                    } else if (endTimeSec > lastRange.endSec) {
                        //! Right-extension: the chart's right edge grew. In live/paper mode a
                        //! NORMAL forward tick advances it by ~one bar — that bar's indicator
                        //! value arrives via the liveNodeResults WS merge below, so skipping
                        //! REST here avoids a full-range recompute on every candle close.
                        //!
                        //! #50-2: a #50 gap-repair merge (WS reconnect / backend-restart
                        //! backfill via refetchMissedRange) can jump the right edge by MANY
                        //! bars at once. The live single-point WS merge only ever sets ONE
                        //! key (liveEvalTimeSec) per message — it cannot backfill the indicator
                        //! values for bars that closed while the tab/socket was gapped. Treating
                        //! that jump as "just a live bar" (old behaviour) skipped REST AND
                        //! falsely marked the whole gap as covered in lastCalcRangeRef, so
                        //! indicatorMaps permanently had no entries there: overlays stopped
                        //! drawing at the gap, and every marker-type node (calculateIndicators
                        //! iterates the merged candle timeline, so a hole in indicatorMaps means
                        //! no entry — see AttachedModeManager) collapsed onto whichever single
                        //! candle DID have a value, i.e. the last one evaluated before the drop.
                        //! A multi-bar jump is therefore routed through the SAME delta REST path
                        //! a right-extension already uses in backtest/rd — no second mechanism.
                        const rightGapSec = endTimeSec - lastRange.endSec;
                        const isSingleLiveBarAdvance = rightGapSec <= tfSec * 1.5;
                        if (isLiveMode && isSingleLiveBarAdvance) {
                            lastCalcRangeRef.current = {
                                startSec: lastRange.startSec,
                                endSec: Math.max(lastRange.endSec, endTimeSec),
                            };
                            prevFunctionalRef.current = functionalKey;
                            activeRequestIdRef.current = '';
                            setIsCalculating(false);
                            return;
                        }
                        // Pagination extended RIGHT (or a live gap-repair jump): request from
                        // old end - overlap to new end, same as backtest/rd right-extension.
                        startTimeSec = lastRange.endSec - warmupSec;
                        isDeltaRequest = true;
                    }
                }
                if (!isDeltaRequest) {
                    // Full range request (initial load or strategy change)
                    startTimeSec -= (tfSec * 500);
                    if (expectedStartDate) startTimeSec = Math.min(startTimeSec, new Date(expectedStartDate).getTime() / 1000);
                    if (expectedEndDate) {
                        endTimeSec = new Date(expectedEndDate).getTime() / 1000;
                        if (isReplayContext) endTimeSec += (24 * 3600);
                    }
                }

                const payload = {
                    instrument_id: instrument.instrument_id || `${instrument.exchange}:${instrument.symbol}:${instrument.instrument_type || 'EQUITY'}:_:_:_`,
                    timeframe,
                    start_date: new Date(startTimeSec * 1000).toISOString(),
                    end_date: new Date(endTimeSec * 1000).toISOString(),
                    indicators: activeIndicators.map(ind => ({ type: ind.type, params: ind.params || {} })),
                    ...(instruments !== undefined ? { instruments } : {}),
                    ...(general !== undefined ? { general } : {}),
                    nodes: (nodes || []).map(n => {
                        const { styleOptions, visual_settings, color, label, ...functionalData } = (n.data || {});
                        if (n.type === 'constant' && functionalData.value !== undefined) functionalData.value = functionalData.value;
                        return { id: n.id, type: n.type, data: functionalData };
                    }),
                    edges: (edges || []).map(e => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle })),
                    ...(replayMode ? { mode: replayMode, run_id: replayRunId } : { mode: 'live' }),
                    ...(rolloverDays !== undefined ? { rollover_days: parseInt(rolloverDays, 10) } : {}),
                    //--> Incremental mode: only compute changed/new nodes instead of full graph
                    ...((!isDeltaRequest && incrementalInfo.canIncremental) ? {
                        changed_nodes: incrementalInfo.changedNodeIds,
                        calc_intent: 'strategy_edit_incremental'
                    } : {})
                };

                const response = await services.indicators.calculate(payload, { signal: controller.signal });
                if (controller.signal.aborted) return;

                const responseRoot = response?.data;
                const nestedRoot = responseRoot?.data;
                const statusValue = responseRoot?.status || nestedRoot?.status;
                const resultsArray = Array.isArray(responseRoot?.data) ? responseRoot.data : (Array.isArray(nestedRoot?.data) ? nestedRoot.data : []);

                //--> Detect if this was an incremental response from backend
                const isIncrementalResponse = responseRoot?.incremental === true || nestedRoot?.incremental === true;

                if (statusValue === 'success') {
                    setCalculateError(null);
                    // FIX: Detection of pagination must compare against the last SUCCESSFUL structural state
                    const isPagination = isStrategyUnchanged;

                    // Update Refs
                    prevFunctionalRef.current = functionalKey;
                    prevGraphFunctionalKeyRef.current = graphFunctionalKey;
                    lastSuccessfulFunctionalKeyRef.current = functionalKey;
                    //--> Track the FULL data extent we now have indicator coverage for
                    lastCalcRangeRef.current = {
                        startSec: Math.min(hasPriorRange ? lastRange.startSec : actualStart, actualStart),
                        endSec: Math.max(hasPriorRange ? lastRange.endSec : actualEnd, actualEnd)
                    };
                    //--> Update incremental tracking refs for next change detection
                    prevNodeFingerprintsRef.current = incrementalInfo.currentFingerprints;
                    prevEdgeSignatureRef.current = incrementalInfo.currentEdgeSig;
                    prevActiveIndicatorIdsRef.current = incrementalInfo.currentIndicatorIds;

                    // Update Maps safely to prevent indicator drift/data loss during pagination
                    setIndicatorMaps(prev => {
                        const nextMaps = { ...prev };
                        //--> In incremental mode, preserve all existing maps and only update/add new ones
                        const shouldMerge = isPagination || isIncrementalResponse;

                        resultsArray.forEach((result) => {
                            const existingMap = prev[result.indicator_id];
                            // Merge if it's pagination or incremental, otherwise start fresh
                            const tempMap = (shouldMerge && existingMap) ? new Map(existingMap) : new Map();

                            (result.values || []).forEach(p => {
                                const t = getTimeSeconds(p);
                                let scalarValue = p.value;
                                if (typeof scalarValue === 'object' && scalarValue?.value !== undefined) scalarValue = scalarValue.value;
                                if (typeof scalarValue === 'string') {
                                    try { scalarValue = JSON.parse(scalarValue); } catch (e) { scalarValue = Number(scalarValue); }
                                }
                                if (typeof scalarValue === 'boolean') scalarValue = scalarValue ? 1 : 0;

                                const isValueValid = scalarValue !== null && scalarValue !== undefined &&
                                    (typeof scalarValue === 'object' || !isNaN(scalarValue));

                                if (isValueValid) {
                                    tempMap.set(t, scalarValue);
                                }
                            });
                            nextMaps[result.indicator_id] = tempMap;
                        });
                        return nextMaps;
                    });
                }
            } catch (e) {
                if (e.name !== 'AbortError' && e.message !== 'canceled') {
                    console.error("Failed to calculate indicators:", e);
                    const detail = e.response?.data?.detail;
                    setCalculateError({
                        status: e.response?.status ?? e.status ?? null,
                        message: detail?.error?.message
                            || detail?.message
                            || (typeof detail === 'string' ? detail : null)
                            || e.response?.data?.message
                            || e.message
                            || 'Failed to calculate indicators.',
                    });
                    prevFunctionalRef.current = functionalKey;
                }
            } finally {
                if (activeRequestIdRef.current === functionalKey) activeRequestIdRef.current = '';
                setIsCalculating(false);
            }
        };

        const timer = setTimeout(fetchIndicatorData, 200);
        return () => {
            clearTimeout(timer);
            if (abortControllerRef.current) abortControllerRef.current.abort();
        };
    }, [priceData, activeIndicators, instrument, timeframe, edges, nodes, graphFunctionalKey, expectedStartDate, expectedEndDate, replayMode, replayRunId, rolloverDays, getTimeSeconds, incrementalInfo, chartMode]);

    //--> Merge live worker node_results into indicatorMaps[nodeId] keyed by bar-close epoch seconds.
    //    No-op in backtest mode (liveNodeResults null). Live values overwrite historic at collision.
    useEffect(() => {
        if (!liveNodeResults || Object.keys(liveNodeResults).length === 0) return;
        if (!liveEvalTimeSec) return;
        //--> TF mismatch guard. WS messages are stamped at deployment-TF cadence;
        //*  merging them into a finer-grained chart Map (e.g. 5m WS into 1m chart)
        //*  produces V-shapes at every deployment-TF boundary because the worker
        //*  computes T3 etc. on a different OHLC granularity than the cache.
        //*  Cached values (computed at each indicator's native TF by /indicators/
        //*  calculate) are correct — let them stand.
        if (liveBarTimeframe && timeframe && liveBarTimeframe !== timeframe) return;
        setIndicatorMaps((prev) => {
            const next = { ...prev };
            for (const [nodeId, result] of Object.entries(liveNodeResults)) {
                //* 142-42: a reduced multi-output node merges its FULL outputs (metadata.outputs) —
                //*   the same row shape /indicators/calculate returns — never the bare configured scalar
                const raw = liveNodeChartValue(result);
                if (raw === null || raw === undefined) continue;
                const existing = next[nodeId] instanceof Map ? new Map(next[nodeId]) : new Map();
                existing.set(liveEvalTimeSec, raw);
                next[nodeId] = existing;
            }
            return next;
        });
    //! #50-2: `nodes` is NOT read inside this effect (only liveNodeResults/
    //! liveEvalTimeSec/liveBarTimeframe/timeframe are) and must stay out of the
    //! dependency array. `nodes` is a fresh array reference on every render in
    //! several real call sites (and in every test harness) — with it included,
    //! this effect re-fires every render (setIndicatorMaps always returns a new
    //! object even for identical data → state changes → re-render → effect
    //! fires again), which is an infinite-render loop, not just a wasted call.
    }, [liveNodeResults, liveEvalTimeSec, liveBarTimeframe, timeframe]);

    // Transformation / Extraction for Chart Components
    // NEW: perform the 1:1 timeline alignment on-demand to avoid main-thread blockage
    const calculateIndicators = useCallback((indicator, fullData, targetTime = null) => {
        const id = indicator.id;
        const map = indicatorMaps[id];
        
        // Single point lookup (O(1) normally, O(N) optimized scan-back for overlays)
        if (targetTime !== null) {
            if (!map) return null;
            
            // Check timeframe interval for fuzzy matching (Dashboard tooltip)
            const tfMatch = (timeframe || '1m').match(/(\d+)([mhd])/);
            let intSec = 60; 
            if (tfMatch) {
                const v = parseInt(tfMatch[1]);
                const u = tfMatch[2];
                if (u === 'm') intSec = v * 60;
                else if (u === 'h') intSec = v * 3600;
                else if (u === 'd') intSec = v * 86400;
            }

            // 1. Precise/Fuzzy Match (Standard)
            const val = map.get(targetTime) ?? map.get(targetTime - intSec) ?? map.get(targetTime + intSec);
            if (val !== undefined && val !== null) return val;

            // 2. Scan-back (RD-consistent LOCV)
            // If it's an overlay/indicator lookup, we look back up to 24 hours in 1m steps
            // This is crucial for HTF visibility (e.g. 30m slope shown on 1m chart)
            // NOTE: step uses intSec (indicator TF interval) for efficiency; map.has is O(1).
            for (let t = targetTime - intSec; t >= targetTime - (24 * 3600); t -= intSec) {
                if (map.has(t)) return map.get(t);
            }

            return null;
        }

        // Entire Timeline Alignment (O(N) - on demand)
        const rawArray = []; // Empty fallback
        if (!map) return rawArray;

        // Determine timeframe interval for fuzzy matching (Start of bar vs End of bar)
        const tfMatch = (timeframe || '1m').match(/(\d+)([mhd])/);
        let intervalSec = 60;
        if (tfMatch) {
            const val = parseInt(tfMatch[1]);
            const unit = tfMatch[2];
            if (unit === 'm') intervalSec = val * 60;
            if (unit === 'h') intervalSec = val * 3600;
            if (unit === 'd') intervalSec = val * 86400;
        }

        if (fullData && fullData.length > 0) {
            const resultList = fullData.map(candle => {
                const t = getTimeSeconds(candle);
                // Fuzzy match: check exact time, then +/- 1 interval to catch start/end bar shifts
                const val = map.get(t) ?? map.get(t - intervalSec) ?? map.get(t + intervalSec);
                return val !== undefined && val !== null ? { time: t, value: val } : { time: t };
            });
            return resultList;
        }

        return rawArray;
    }, [indicatorMaps, getTimeSeconds]);

    const clearIndicatorData = useCallback(() => {
        setIndicatorMaps({});
        setCalculateError(null);
        prevFunctionalRef.current = '';
        prevGraphFunctionalKeyRef.current = '';
        lastCalcRangeRef.current = { startSec: 0, endSec: 0 };
    }, []);

    return {
        indicators: activeIndicators,
        activeIndicators,
        removeIndicator,
        updateIndicatorSettings,
        indicatorMaps, // Exposing Map for O(1) Dashboard lookups
        calculateIndicators,
        clearIndicatorData,
        isCalculating, // Exposed so SmartChart can show loading feedback during recalculation
        calculateError
    };
}
